import { Capabilities, PlatformType } from "@spawn/common";
import {
	Align,
	I18NextProvider,
	IAssetHandle,
	Orientation,
	PlaySpine,
	SequenceChain,
	SpineAnimation,
	SpineResource,
	View,
	ViewFixedSizeScalable,
	asset,
	inject,
	subscription,
	view,
} from "@spawn/core";
import { SlideView } from "@spawn/slot-machine";
import Color from "color";
import { Subscription } from "rxjs";
import Victor from "victor";
import { createTextHighlightSegments, getMaxBonusMultiplier, Layout, TextStyles } from "@spawn/nrg-games-core";

@view
export class LoadingScreenFeatureInfo extends SlideView {
	@inject private readonly i18n!: I18NextProvider;

	@asset(SpineResource) public readonly bonusSpine!: IAssetHandle<SpineResource>;
	@asset(SpineResource) public readonly nudgingSpine!: IAssetHandle<SpineResource>;
	@asset(SpineResource) public readonly scatterSpine!: IAssetHandle<SpineResource>;

	@subscription protected subscriptions: Subscription[] = [];

	private nudgingView!: View;
	private bonusView!: View;
	private scatterView!: View;
	private nudgingAnimation!: SpineAnimation;
	private bonusAnimation!: SpineAnimation;
	private scatterAnimation!: SpineAnimation;
	private animationChain?: SequenceChain;

	constructor() {
		super({
			options: {
				layout: new Layout(Align.CenterHorizontal, Align.CenterVertical),
			},
			dimmingOptions: {
				color: new Color("#331100"),
				alpha: 0.2,
			},
			addBranding: false,
		});
		this.addNudgingView();
		this.addBonusView();
		this.addScatterView();

		this.subscriptions.push(this.screenOrientationService.orientationChanged$.subscribe(() => this.onOrientationChange()));
		this.onOrientationChange();
	}

	public override set alpha(alpha: number) {
		super.alpha = alpha;
		alpha > 0 ? this.playAnimations() : this.stopAnimations();
	}

	private addNudgingView(): void {
		this.nudgingView = new ViewFixedSizeScalable({ layout: new Layout(Align.CenterHorizontal, Align.CenterVertical) });
		this.nudgingAnimation = new SpineAnimation(this.nudgingSpine());
		this.nudgingAnimation.skinName = "default";

		const text = TextStyles.default(this.i18n.t("nudgenreveal.text9"), {
			fontSize: 36,
			wordWrap: true,
			wordWrapWidth: 500,
			align: "center",
			layout: new Layout(Align.CenterHorizontal, Align.CenterVertical),
		});

		text.position = new Victor(0, 280);
		this.nudgingAnimation.scale = new Victor(0.6, 0.6);
		this.nudgingAnimation.position = new Victor(-80, -250);

		this.nudgingView.addChild(text, this.nudgingAnimation);
		this.addChild(this.nudgingView);
	}

	private createBonusInfoText(): View {
		const container = new View({
			layout: Layout.center(),
		});

		const multi = getMaxBonusMultiplier() + "x";
		const textSegments = createTextHighlightSegments(this.i18n.t("misc.text3", { bet: multi }), multi);

		// Segment[1] position needs to be dependent on segment[0] as the text there can
		// be multiline. The height calculation seems to be off, however.
		textSegments.segments[0].position = new Victor(0, -20);
		textSegments.segments[1].position = new Victor(0, textSegments.typeOfFirstWord === "default" ? 30 : 55);

		container.addChild(...textSegments.segments);

		for (const segment of textSegments.segments) {
			segment._pixiObject.style.wordWrap = false;
			segment._pixiObject.style.align = "center";
		}

		return container;
	}

	private addBonusView(): void {
		this.bonusView = new View({ layout: new Layout(Align.CenterHorizontal, Align.CenterVertical) });
		this.bonusAnimation = new SpineAnimation(this.bonusSpine(), new Layout(Align.CenterHorizontal, Align.CenterVertical));
		this.bonusAnimation.skinName = "default";
		const text = this.createBonusInfoText();

		text.position = new Victor(40, 300);
		text.scale = new Victor(0.75, 0.75);

		this.bonusAnimation.scale =
			this.screenOrientationService.orientation === Orientation.Portrait ? new Victor(1.15, 1.15) : new Victor(1, 1);
		this.bonusView.addChild(text, this.bonusAnimation);

		this.addChild(this.bonusView);
	}

	private addScatterView(): void {
		this.scatterView = new ViewFixedSizeScalable({ layout: new Layout(Align.CenterHorizontal, Align.CenterVertical) });
		this.scatterAnimation = new SpineAnimation(this.scatterSpine());
		this.scatterAnimation.skinName = "introscreen_scatter";
		this.scatterAnimation.position = new Victor(-250, -150);
		// fixes an issue where the animation looks bad on first load while the others are playing
		this.scatterAnimation.play("introscreen_scatter");
		this.scatterAnimation.stop();

		const text = TextStyles.default(this.i18n.t("nudgenreveal.intro1"), {
			fontSize: 36,
			wordWrap: true,
			wordWrapWidth: 600,
			align: "center",
			layout: new Layout(Align.CenterHorizontal, Align.CenterVertical),
		});

		text.position = new Victor(0, 257);
		this.scatterView.addChild(text, this.scatterAnimation);

		this.addChild(this.scatterView);
	}

	private playAnimations(): void {
		this.stopAnimations();

		this.animationChain = new SequenceChain(
			new PlaySpine(this.nudgingAnimation, "intro_mystery"),
			new PlaySpine(this.bonusAnimation, "intro_bonus"),
			new PlaySpine(this.scatterAnimation, "introscreen_scatter")
		);
		void this.animationChain.play();
	}

	private stopAnimations(): void {
		if (this.animationChain) {
			this.animationChain.cancel();
		}

		this.nudgingAnimation?.stop();
		this.bonusAnimation?.stop();
		this.scatterAnimation?.stop();
	}

	private onOrientationChange(): void {
		if (
			Capabilities.platformType === PlatformType.Desktop ||
			this.screenOrientationService.orientation === Orientation.Landscape
		) {
			this.nudgingView.position = new Victor(-520, -150);
			this.scatterView.position = new Victor(520, -150);
			this.bonusView.position = new Victor(0, -150);
			this.nudgingView.scale = new Victor(1, 1);
			this.bonusView.scale = new Victor(1, 1);
			this.scatterView.scale = new Victor(1, 1);
		} else if (this.screenOrientationService.orientation === Orientation.Portrait) {
			if (Capabilities.platformType === PlatformType.Mobile && window.innerWidth > 375) {
				this.nudgingView.position = new Victor(-250, -450);
				this.bonusView.position = new Victor(200, -500);
				this.scatterView.position = new Victor(0, 180);

				this.nudgingView.scale = new Victor(1.15, 1.15);
				this.bonusView.scale = new Victor(1.15, 1.15);
				this.scatterView.scale = new Victor(1.15, 1.15);
			} else {
				this.nudgingView.position = new Victor(-250, -350);
				this.bonusView.position = new Victor(200, -350);
				this.scatterView.position = new Victor(0, 150);
			}
		} else {
			this.bonusView.position = new Victor(0, 0);
			this.scatterView.position = new Victor(0, 0);
			this.nudgingView.position = new Victor(0, 0);
		}
	}
}
